(() => {
  const namespace = window.AerreRewards;

  if (!namespace || !namespace.config || namespace.controller) {
    return;
  }

  class AerreRewardsController {
    constructor(config) {
      this.config = config;
      this.gift = this.normalizeGiftConfig(config.gift);
      this.cart = this.normalizeCart(config.initialCart);
      this.adapter = this.resolveAdapter();
      this.confirmed = this.inspectCart(this.cart).coherent;
      this.desired = this.desiredFromCart(this.cart);
      this.desiredGiftVariantId = this.confirmedGiftVariantId(this.cart) || this.preferredGiftVariantId(this.cart) || this.gift.defaultVariantId;
      this.requested = null;
      this.processing = null;
      this.pending = false;
      this.checkoutInProgress = false;
      this.lastError = '';
      this.giftLastError = '';
      this.giftAnnouncement = '';
      this.lastSurface = null;
      this.giftFocusElement = null;
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
    }

    start() {
      document.documentElement.dataset.aerreRewardsCohort = this.adapter.cohort;
      document.documentElement.dataset.aerreRewardsCountry = this.adapter.countryCode;
      document.documentElement.dataset.aerreRewardsCountryEligible = String(this.adapter.countryEligible);
      namespace.adapter = Object.freeze({ ...this.adapter });
      document.addEventListener('change', this.onChange);
      document.addEventListener('click', this.onClick);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.documentElement.addEventListener('cart:updated', this.onCartUpdated);
      this.updateUi();

      const treatmentEligible = this.adapter.countryEligible && this.adapter.cohort === 'treatment' && !this.config.entitled;

      if (!treatmentEligible && this.hasExperimentState(this.cart)) {
        this.enqueue(false);
      } else if (this.operational) {
        this.enqueue(this.desired);
      } else if (this.hasManagedStaleState(this.cart)) {
        this.enqueue(false);
      }
    }

    get operational() {
      const inspection = this.inspectCart(this.cart);
      return this.adapter.countryEligible && this.adapter.cohort === 'treatment' && this.config.valid && !this.config.entitled && (this.config.available || inspection.membershipItems.length > 0);
    }

    normalizeCart(cart) {
      const normalized = cart && typeof cart === 'object' ? cart : {};
      normalized.items = Array.isArray(normalized.items) ? normalized.items : [];
      normalized.attributes = normalized.attributes && typeof normalized.attributes === 'object' ? normalized.attributes : {};
      return normalized;
    }

    normalizeGiftConfig(gift) {
      const normalized = gift && typeof gift === 'object' ? gift : {};
      normalized.enabled = Boolean(normalized.enabled);
      normalized.valid = Boolean(normalized.valid);
      normalized.productId = Number(normalized.productId);
      normalized.property = String(normalized.property || '');
      normalized.version = String(normalized.version || '');
      normalized.variants = Array.isArray(normalized.variants)
        ? normalized.variants
          .map((variant) => ({
            id: Number(variant.id),
            title: String(variant.title || ''),
            available: Boolean(variant.available)
          }))
          .filter((variant) => Number.isFinite(variant.id))
        : [];
      normalized.defaultVariantId = Number(normalized.defaultVariantId);

      if (!normalized.variants.some((variant) => variant.id === normalized.defaultVariantId && variant.available)) {
        normalized.defaultVariantId = normalized.variants.find((variant) => variant.available)?.id || normalized.variants[0]?.id || null;
      }

      return normalized;
    }

    resolveAdapter() {
      const experiment = window.AerreRewardsExperiment && typeof window.AerreRewardsExperiment === 'object'
        ? window.AerreRewardsExperiment
        : {};
      let cohort = this.config.defaultEnabled ? 'treatment' : 'control';
      let defaultOn = Boolean(this.config.defaultOn);

      if (typeof window.AERRE_MEMBERSHIP_OFFER_ENABLED === 'boolean') {
        cohort = window.AERRE_MEMBERSHIP_OFFER_ENABLED ? 'treatment' : 'control';
      }

      if (experiment.cohort === 'control' || experiment.cohort === 'treatment') {
        cohort = experiment.cohort;
      } else if (typeof experiment.enabled === 'boolean') {
        cohort = experiment.enabled ? 'treatment' : 'control';
      }

      if (typeof experiment.defaultOn === 'boolean') {
        defaultOn = experiment.defaultOn;
      }

      if (this.config.previewAllowed) {
        const preview = new URLSearchParams(window.location.search).get('aerre_rewards_preview');

        if (preview === 'control') {
          cohort = 'control';
        } else if (preview === 'treatment') {
          cohort = 'treatment';
        } else if (preview === 'on') {
          cohort = 'treatment';
          defaultOn = true;
        } else if (preview === 'off') {
          cohort = 'treatment';
          defaultOn = false;
        }
      }

      const countryCode = String(this.config.countryCode || '').trim().toUpperCase();
      const experimentCountryCode = String(this.config.experimentCountryCode || '').trim().toUpperCase();
      const countryEligible = Boolean(experimentCountryCode) && countryCode === experimentCountryCode;

      if (!countryEligible) {
        cohort = 'control';
        defaultOn = false;
      }

      return { cohort, defaultOn, countryCode, experimentCountryCode, countryEligible };
    }

    sellingPlanId(item) {
      return item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? Number(item.selling_plan_allocation.selling_plan.id)
        : null;
    }

    isMembershipItem(item) {
      return Number(item.product_id) === Number(this.config.productId) || Number(item.variant_id) === Number(this.config.variantId);
    }

    isValidMembershipItem(item) {
      return Number(item.variant_id) === Number(this.config.variantId) && this.sellingPlanId(item) === Number(this.config.sellingPlanId);
    }

    giftVariant(variantId) {
      return this.gift.variants.find((variant) => variant.id === Number(variantId)) || null;
    }

    isManagedGiftItem(item) {
      const properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};

      return Number(item && item.product_id) === this.gift.productId
        && Boolean(this.giftVariant(item && item.variant_id))
        && String(properties[this.gift.property] || '') === this.gift.version;
    }

    confirmedGiftVariantId(cart) {
      const giftItems = this.inspectCart(cart).giftItems;

      if (giftItems.length !== 1 || Number(giftItems[0].quantity) !== 1) {
        return null;
      }

      return Number(giftItems[0].variant_id);
    }

    preferredGiftVariantId(cart) {
      const giftItems = this.inspectCart(cart).giftItems;
      const variantIds = giftItems
        .map((item) => Number(item.variant_id))
        .filter((variantId, index, values) => values.indexOf(variantId) === index);

      return variantIds.length === 1 ? variantIds[0] : null;
    }

    giftStateSafeForCheckout(cart, target) {
      const inspection = this.inspectCart(cart);

      if (inspection.giftItems.length === 0) {
        return true;
      }

      return Boolean(
        target
        && inspection.coherent
        && this.gift.enabled
        && this.gift.valid
        && inspection.giftItems.length === 1
        && Number(inspection.giftItems[0].quantity) === 1
      );
    }

    membershipStateSafeForCheckout(cart) {
      const inspection = this.inspectCart(cart);
      return inspection.membershipItems.length === 0 || inspection.coherent;
    }

    hasManagedStaleState(cart) {
      const inspection = this.inspectCart(cart);

      if (!this.operational) {
        return inspection.membershipItems.length > 0 || inspection.giftItems.length > 0;
      }

      const membershipIsStale = inspection.membershipItems.length > 0 && !inspection.coherent;
      const giftIsStale = inspection.giftItems.length > 0 && !this.giftStateSafeForCheckout(cart, true);

      return membershipIsStale || giftIsStale;
    }

    inspectCart(cart) {
      const normalized = this.normalizeCart(cart);
      const membershipItems = normalized.items.filter((item) => this.isMembershipItem(item));
      const validItems = membershipItems.filter((item) => this.isValidMembershipItem(item));
      const giftItems = normalized.items.filter((item) => this.isManagedGiftItem(item));
      const hasMerchandise = normalized.items.some((item) => !this.isMembershipItem(item) && !this.isManagedGiftItem(item) && Number(item.quantity) > 0);
      const coherent = membershipItems.length === 1 && validItems.length === 1 && Number(validItems[0].quantity) === 1;

      return { membershipItems, validItems, giftItems, hasMerchandise, coherent };
    }

    attributeValue(cart, name) {
      const attributes = this.normalizeCart(cart).attributes;
      const value = attributes[name];
      return value === null || typeof value === 'undefined' ? '' : String(value);
    }

    hasExperimentState(cart) {
      const inspection = this.inspectCart(cart);
      const attributeNames = [
        this.config.preferenceAttribute,
        this.config.versionAttribute,
        this.config.assignmentAttribute,
        'membership_opt_in',
        'membership_opt_in_source'
      ];

      return inspection.membershipItems.length > 0 || inspection.giftItems.length > 0 || attributeNames.some((name) => this.attributeValue(cart, name) !== '');
    }

    desiredFromCart(cart) {
      const inspection = this.inspectCart(cart);

      if (!inspection.hasMerchandise || this.config.entitled || !this.adapter.countryEligible || this.adapter.cohort !== 'treatment') {
        return false;
      }

      const preference = this.attributeValue(cart, this.config.preferenceAttribute);

      if (preference === `off:${this.config.offerVersion}`) {
        return false;
      }

      if (preference === `on:${this.config.offerVersion}`) {
        return true;
      }

      return this.adapter.defaultOn;
    }

    targetAttributes(target, hasMerchandise) {
      const attributes = {
        membership_opt_in: '',
        membership_opt_in_source: ''
      };

      if (!hasMerchandise) {
        attributes[this.config.preferenceAttribute] = '';
        attributes[this.config.versionAttribute] = '';
        attributes[this.config.assignmentAttribute] = '';
        return attributes;
      }

      if (!this.adapter.countryEligible) {
        attributes[this.config.preferenceAttribute] = `off:${this.config.offerVersion}`;
        attributes[this.config.versionAttribute] = this.config.offerVersion;
        attributes[this.config.assignmentAttribute] = '';
        return attributes;
      }

      attributes[this.config.preferenceAttribute] = `${target ? 'on' : 'off'}:${this.config.offerVersion}`;
      attributes[this.config.versionAttribute] = this.config.offerVersion;
      attributes[this.config.assignmentAttribute] = this.adapter.cohort;
      return attributes;
    }

    attributesMatch(cart, expected) {
      return Object.entries(expected).every(([name, value]) => this.attributeValue(cart, name) === value);
    }

    sectionsPayload() {
      const sections = Array.from(document.querySelectorAll('[data-aerre-rewards-section-id]'))
        .map((component) => component.dataset.aerreRewardsSectionId)
        .filter((sectionId, index, values) => sectionId && values.indexOf(sectionId) === index)
        .slice(0, 5);

      return {
        sections,
        sections_url: window.location.pathname + window.location.search
      };
    }

    async post(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data) {
        const message = data && (data.description || data.message) ? data.description || data.message : response.statusText;
        throw new Error(message || 'Aerre Rewards cart request failed');
      }

      return data;
    }

    async updateCart(updates, attributes) {
      const body = { ...this.sectionsPayload() };

      if (updates && Object.keys(updates).length > 0) {
        body.updates = updates;
      }

      if (attributes) {
        body.attributes = attributes;
      }

      const cart = await this.post(`${this.config.cartUrl}/update.js`, body);

      if (!Array.isArray(cart.items)) {
        throw new Error('Aerre Rewards received an invalid cart response');
      }

      return this.normalizeCart(cart);
    }

    async addMembership() {
      return this.post(`${this.config.cartAddUrl}.js`, {
        items: [
          {
            id: this.config.variantId,
            quantity: 1,
            selling_plan: this.config.sellingPlanId,
            properties: {
              _aerre_rewards_offer: this.config.offerVersion
            }
          }
        ]
      });
    }

    async addGift(variantId) {
      return this.post(`${this.config.cartAddUrl}.js`, {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [this.gift.property]: this.gift.version
            }
          }
        ]
      });
    }

    async recoverCart() {
      const response = await fetch(`${this.config.cartUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Aerre Rewards cart recovery failed');
      }

      const cart = await response.json();

      if (!Array.isArray(cart.items)) {
        throw new Error('Aerre Rewards received an invalid recovery response');
      }

      return this.normalizeCart(cart);
    }

    async refreshCartSections(cart) {
      const normalized = this.normalizeCart(cart);

      if (normalized.sections) {
        return normalized;
      }

      const attributeName = String(this.config.preferenceAttribute || '');

      if (attributeName) {
        return this.updateCart({}, {
          [attributeName]: this.attributeValue(normalized, attributeName)
        });
      }

      const firstItem = normalized.items.find((item) => Number(item.quantity) > 0);
      return this.updateCart(firstItem ? { [firstItem.key]: Number(firstItem.quantity) } : {}, {});
    }

    async recoverCartWithSections() {
      const cart = await this.recoverCart();

      try {
        return await this.refreshCartSections(cart);
      } catch (error) {
        console.error('Aerre Rewards section recovery:', error);
        return cart;
      }
    }

    async removeManagedGifts(cart) {
      const inspection = this.inspectCart(cart);

      if (inspection.giftItems.length === 0) {
        return this.refreshCartSections(cart);
      }

      const updates = {};
      inspection.giftItems.forEach((item) => {
        updates[item.key] = 0;
      });

      const updatedCart = await this.updateCart(updates, null);

      if (this.inspectCart(updatedCart).giftItems.length > 0) {
        throw new Error('Aerre Rewards gift lines could not be removed');
      }

      return updatedCart;
    }

    async removeManagedCartLines(cart) {
      const inspection = this.inspectCart(cart);
      const managedItems = [...inspection.membershipItems, ...inspection.giftItems];

      if (managedItems.length === 0) {
        return this.refreshCartSections(cart);
      }

      const updates = {};
      managedItems.forEach((item) => {
        updates[item.key] = 0;
      });

      const updatedCart = await this.updateCart(updates, null);
      const updatedInspection = this.inspectCart(updatedCart);

      if (updatedInspection.membershipItems.length > 0 || updatedInspection.giftItems.length > 0) {
        throw new Error('Aerre Rewards managed cart lines could not be removed');
      }

      return updatedCart;
    }

    async rollbackGiftReplacement(previousGiftItems) {
      let cart = await this.recoverCart();
      let inspection = this.inspectCart(cart);
      const previousKeys = new Set(previousGiftItems.map((item) => item.key));
      const previousVariantIds = previousGiftItems
        .map((item) => Number(item.variant_id))
        .filter((variantId, index, values) => values.indexOf(variantId) === index);
      let keeper = inspection.giftItems.find((item) => previousKeys.has(item.key)) || null;

      if (!keeper && previousVariantIds.length === 1) {
        keeper = inspection.giftItems.find((item) => Number(item.variant_id) === previousVariantIds[0]) || null;
      }

      const updates = {};
      inspection.giftItems.forEach((item) => {
        updates[item.key] = keeper && item.key === keeper.key ? 1 : 0;
      });

      if (inspection.giftItems.length > 0) {
        cart = await this.updateCart(updates, null);
      } else {
        cart = await this.refreshCartSections(cart);
      }

      inspection = this.inspectCart(cart);

      if (inspection.giftItems.length > 1 || (inspection.giftItems.length === 1 && Number(inspection.giftItems[0].quantity) !== 1)) {
        throw new Error('Aerre Rewards gift replacement rollback failed');
      }

      return cart;
    }

    giftSelectionMessage(variantId) {
      const variant = this.giftVariant(variantId);
      const template = String(this.config.strings.giftSelected || '');

      return template
        .replace('__AERRE_GIFT_COLOUR__', variant ? variant.title : '')
        .replace(/\{\{\s*colour\s*\}\}/g, variant ? variant.title : '');
    }

    desiredAvailableGiftVariant(cart) {
      const requested = this.giftVariant(this.desiredGiftVariantId);
      const inspection = this.inspectCart(cart);
      const requestedAlreadyInCart = requested && inspection.giftItems.some((item) => Number(item.variant_id) === requested.id);

      if (requested && (requested.available || requestedAlreadyInCart)) {
        return requested.id;
      }

      return this.gift.variants.find((variant) => variant.available)?.id || null;
    }

    async reconcileGift(cart, target) {
      let inspection = this.inspectCart(cart);
      const shouldHaveGift = Boolean(target && inspection.coherent && this.gift.enabled && this.gift.valid);

      if (!shouldHaveGift) {
        if (inspection.giftItems.length > 0) {
          const updates = {};
          inspection.giftItems.forEach((item) => {
            updates[item.key] = 0;
          });
          cart = await this.updateCart(updates, null);
        }

        this.desiredGiftVariantId = this.gift.defaultVariantId;
        this.giftLastError = '';
        this.giftAnnouncement = '';
        return cart;
      }

      const desiredVariantId = this.desiredAvailableGiftVariant(cart);

      if (!desiredVariantId) {
        this.giftLastError = this.config.strings.giftUnavailable;
        this.giftAnnouncement = '';
        return cart;
      }

      this.desiredGiftVariantId = desiredVariantId;
      let keeper = inspection.giftItems.find((item) => Number(item.variant_id) === desiredVariantId) || null;
      const previousGiftItems = inspection.giftItems.map((item) => ({
        key: item.key,
        variant_id: Number(item.variant_id)
      }));
      let replacementAttempted = false;

      try {
        if (!keeper) {
          // Add the replacement first. If any later step fails, roll back the
          // newly attempted colour and keep at most one previously confirmed gift.
          replacementAttempted = true;
          await this.addGift(desiredVariantId);
          cart = await this.recoverCart();
          inspection = this.inspectCart(cart);
          keeper = inspection.giftItems.find((item) => Number(item.variant_id) === desiredVariantId) || null;

          if (!keeper) {
            throw new Error('Aerre Rewards gift line was not confirmed');
          }
        }

        const updates = {};
        inspection.giftItems.forEach((item) => {
          updates[item.key] = item.key === keeper.key ? 1 : 0;
        });

        const needsUpdate = inspection.giftItems.some((item) => Number(updates[item.key]) !== Number(item.quantity));
        const needsSectionRefresh = !cart.sections;

        if (needsUpdate || needsSectionRefresh) {
          cart = await this.updateCart(needsUpdate ? updates : { [keeper.key]: 1 }, null);
        }

        inspection = this.inspectCart(cart);

        if (inspection.giftItems.length !== 1 || Number(inspection.giftItems[0].variant_id) !== desiredVariantId || Number(inspection.giftItems[0].quantity) !== 1) {
          throw new Error('Aerre Rewards gift line could not be reconciled');
        }
      } catch (error) {
        if (replacementAttempted) {
          try {
            await this.rollbackGiftReplacement(previousGiftItems);
          } catch (rollbackError) {
            console.error('Aerre Rewards gift rollback:', rollbackError);
          }
        }

        throw error;
      }

      this.giftLastError = '';
      this.giftAnnouncement = this.giftSelectionMessage(desiredVariantId);
      return cart;
    }

    async applyTarget(requestedTarget) {
      let cart = this.cart;
      let inspection = this.inspectCart(cart);
      const target = inspection.hasMerchandise ? requestedTarget : false;
      const attributes = this.targetAttributes(target, inspection.hasMerchandise);
      const updates = {};
      const keeper = target ? inspection.validItems[0] : null;

      inspection.membershipItems.forEach((item) => {
        updates[item.key] = keeper && item.key === keeper.key ? 1 : 0;
      });

      if (!target) {
        inspection.giftItems.forEach((item) => {
          updates[item.key] = 0;
        });
      }

      const needsLineUpdate = Object.keys(updates).some((key) => {
        const item = [...inspection.membershipItems, ...inspection.giftItems].find((candidate) => candidate.key === key);
        return Number(updates[key]) !== Number(item.quantity);
      });
      const needsAdd = target && !keeper;
      const needsAttributeUpdate = !this.attributesMatch(cart, attributes);

      if (needsLineUpdate || (!needsAdd && needsAttributeUpdate)) {
        cart = await this.updateCart(needsLineUpdate ? updates : {}, attributes);
        inspection = this.inspectCart(cart);
      }

      if (needsAdd) {
        const added = await this.addMembership();
        const addedItems = Array.isArray(added.items) ? added.items : [added];
        this.cart = this.normalizeCart({ ...cart, items: [...cart.items, ...addedItems] });
        cart = await this.updateCart({}, attributes);
        inspection = this.inspectCart(cart);
      }

      if (target && !inspection.coherent) {
        throw new Error('Aerre Rewards membership line was not confirmed');
      }

      if (!target && (inspection.membershipItems.length > 0 || inspection.giftItems.length > 0)) {
        throw new Error('Aerre Rewards membership or gift line could not be removed');
      }

      try {
        cart = await this.reconcileGift(cart, target);
      } catch (error) {
        this.giftLastError = this.config.strings.giftError;
        this.giftAnnouncement = '';

        try {
          cart = await this.recoverCartWithSections();
        } catch (recoveryError) {
          console.error('Aerre Rewards gift recovery:', recoveryError);
        }

        // A gift failure must not trap the shopper in the cart. Membership
        // state remains authoritative and ordinary checkout stays available.
        console.error('Aerre Rewards gift:', error);
      }

      return cart;
    }

    enqueue(target) {
      this.desired = Boolean(target);
      this.requested = this.desired;
      this.updateUi();

      if (!this.processing) {
        this.processing = this.processQueue();
      }

      return this.processing;
    }

    async processQueue() {
      while (this.requested !== null) {
        const target = this.requested;
        this.requested = null;
        this.pending = true;
        this.lastError = '';
        this.giftAnnouncement = '';
        this.updateUi();

        try {
          const cart = await this.applyTarget(target);
          this.cart = cart;
          this.confirmed = this.inspectCart(cart).coherent;
          this.lastError = '';
          this.publish(cart);
        } catch (error) {
          this.lastError = this.config.strings.error;

          try {
            this.cart = await this.recoverCartWithSections();
            this.publish(this.cart);
          } catch (recoveryError) {
            console.error('Aerre Rewards recovery:', recoveryError);
          }

          this.confirmed = this.inspectCart(this.cart).coherent;

          if (this.requested === null) {
            this.desired = this.confirmed;
          }

          console.error('Aerre Rewards:', error);
        } finally {
          this.pending = false;
          this.updateUi();
        }
      }

      this.processing = null;
    }

    replaceSections(cart) {
      if (!cart.sections) {
        return;
      }

      Object.values(cart.sections).forEach((sectionHtml) => {
        if (!sectionHtml) {
          return;
        }

        const template = document.createElement('template');
        template.innerHTML = sectionHtml.trim();
        const replacement = template.content.firstElementChild;
        const current = replacement && replacement.id ? document.getElementById(replacement.id) : null;

        // Focal keeps the cart-drawer custom element mounted and refreshes its
        // contents in response to cart:refresh. Replacing the element here
        // would detach that lifecycle before the event is dispatched.
        if (replacement && (replacement.id === 'mini-cart' || replacement.querySelector('#mini-cart'))) {
          return;
        }

        if (replacement && current) {
          current.replaceWith(replacement);
          return;
        }

        const replacementCart = template.content.querySelector('[data-aerre-rewards-cart-section]');
        const currentCart = document.querySelector('[data-aerre-rewards-cart-section]');

        if (replacementCart && currentCart) {
          currentCart.replaceWith(replacementCart);
        }
      });
    }

    publish(cart) {
      this.replaceSections(cart);
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart, aerreRewards: true }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart, aerreRewards: true }
      }));
      window.setTimeout(() => this.updateUi(), 0);
      window.setTimeout(() => this.updateUi(), 800);
    }

    syncBenefits(showTreatment) {
      const source = document.querySelector(
        '.announcement-bar [data-aerre-rewards-cohort-content="treatment"] .membership-benefits-table'
      );
      const sourceMarkup = source ? source.outerHTML : '';

      document.querySelectorAll('[data-aerre-rewards-benefits]').forEach((destination) => {
        const hasBenefits = showTreatment && Boolean(source);
        destination.hidden = !hasBenefits;

        if (!hasBenefits || destination.aerreRewardsBenefitsMarkup === sourceMarkup) {
          return;
        }

        const benefits = source.cloneNode(true);
        benefits.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        destination.replaceChildren(benefits);
        destination.aerreRewardsBenefitsMarkup = sourceMarkup;
      });
    }

    clearGiftFocusIntent() {
      this.lastSurface = null;
      this.giftFocusElement = null;
    }

    restoreGiftSelectorFocus() {
      const selectionFailed = Boolean(this.lastError || this.giftLastError);

      if (!this.lastSurface || (!this.giftAnnouncement && !selectionFailed) || this.pending || this.checkoutInProgress) {
        return;
      }

      const select = document.querySelector(`.aerre-rewards-gift--${this.lastSurface} [data-aerre-rewards-gift-select]`);

      // Wait for Focal to replace the initiating cart surface. A later
      // cart:refresh/section callback will retry against the new selector.
      if (!select || select === this.giftFocusElement || select.disabled) {
        return;
      }

      try {
        select.focus({ preventScroll: true });
      } catch (error) {
        select.focus();
      }

      this.clearGiftFocusIntent();
    }

    updateUi() {
      const showTreatment = this.adapter.countryEligible && this.adapter.cohort === 'treatment';
      const visibleCohort = showTreatment ? 'treatment' : 'control';
      const inspection = this.inspectCart(this.cart);
      const canOffer = this.config.valid && (this.config.available || inspection.membershipItems.length > 0);
      const displayedState = this.pending ? this.desired : this.confirmed;
      const confirmedGiftVariantId = this.confirmedGiftVariantId(this.cart);
      const displayedGiftVariantId = this.pending ? this.desiredGiftVariantId : confirmedGiftVariantId || this.desiredGiftVariantId;
      const hasConfirmedGift = Boolean(confirmedGiftVariantId);
      const showSafetyGift = !this.operational && Boolean(this.lastError) && inspection.coherent && hasConfirmedGift;
      const showGift = inspection.coherent && hasConfirmedGift && this.gift.enabled && this.gift.valid
        && ((showTreatment && displayedState) || showSafetyGift);
      let state = displayedState ? 'on' : 'off';
      let status = displayedState ? this.config.strings.on : this.config.strings.off;
      let giftStatus = this.giftAnnouncement;

      if (this.config.entitled) {
        state = 'active';
        status = this.config.strings.active;
      } else if (!canOffer) {
        state = 'unavailable';
        status = this.config.strings.unavailable;
      } else if (this.lastError) {
        state = 'error';
        status = this.lastError;
      } else if (this.giftLastError && displayedState) {
        state = 'error';
        status = this.giftLastError;
      } else if (this.pending) {
        state = 'updating';
        status = this.config.strings.updating;
      }

      if (this.giftLastError) {
        giftStatus = this.giftLastError;
      } else if (this.pending && showGift) {
        giftStatus = this.config.strings.giftUpdating;
      }

      this.syncBenefits(showTreatment);

      document.querySelectorAll('[data-aerre-rewards-preference]').forEach((component) => {
        component.hidden = !showTreatment;
        component.dataset.state = state;
        component.setAttribute('aria-busy', this.pending ? 'true' : 'false');
        const toggle = component.querySelector('[data-aerre-rewards-toggle]');
        const statusElement = component.querySelector('[data-aerre-rewards-status]');

        if (toggle) {
          toggle.checked = displayedState;
          toggle.disabled = !canOffer || this.checkoutInProgress;
        }

        if (statusElement) {
          statusElement.textContent = status;
        }
      });

      document.querySelectorAll('[data-aerre-rewards-checkout-without]').forEach((button) => {
        button.hidden = !showTreatment || this.config.entitled;
        button.disabled = this.checkoutInProgress;
      });

      document.querySelectorAll('[data-aerre-rewards-checkout]').forEach((button) => {
        button.disabled = this.checkoutInProgress;
        button.setAttribute('aria-busy', this.checkoutInProgress ? 'true' : 'false');
      });

      document.querySelectorAll('[data-aerre-rewards-cohort-content]').forEach((element) => {
        element.hidden = element.dataset.aerreRewardsCohortContent !== visibleCohort;
      });

      document.querySelectorAll('[data-aerre-rewards-gift]').forEach((component) => {
        component.hidden = !showGift;
        component.dataset.state = this.giftLastError ? 'error' : this.pending ? 'updating' : 'ready';
        component.setAttribute('aria-busy', this.pending ? 'true' : 'false');
        const select = component.querySelector('[data-aerre-rewards-gift-select]');
        const statusElement = component.querySelector('[data-aerre-rewards-gift-status]');

        if (select) {
          if (displayedGiftVariantId && this.giftVariant(displayedGiftVariantId)) {
            select.value = String(displayedGiftVariantId);
          }

          Array.from(select.options).forEach((option) => {
            const variant = this.giftVariant(option.value);
            const isConfirmed = Number(option.value) === Number(confirmedGiftVariantId);
            option.disabled = !variant || (!variant.available && !isConfirmed);
          });
          select.disabled = this.pending || this.checkoutInProgress;
        }

        if (statusElement) {
          statusElement.textContent = giftStatus;
        }
      });

      document.querySelectorAll('[data-aerre-rewards-recommendations]').forEach((recommendations) => {
        recommendations.dataset.aerreRewardsSuppressed = String(showGift);
      });

      this.restoreGiftSelectorFocus();
    }

    onChange(event) {
      const giftSelect = event.target.closest('[data-aerre-rewards-gift-select]');
      const toggle = event.target.closest('[data-aerre-rewards-toggle]');

      if (giftSelect) {
        const variant = this.giftVariant(giftSelect.value);
        const inspection = this.inspectCart(this.cart);

        if (!variant || !variant.available || !inspection.coherent || !this.operational) {
          this.updateUi();
          return;
        }

        this.lastSurface = giftSelect.closest('[data-aerre-rewards-gift]')?.classList.contains('aerre-rewards-gift--page') ? 'page' : 'drawer';
        this.giftFocusElement = giftSelect;
        this.desiredGiftVariantId = variant.id;
        this.lastError = '';
        this.giftLastError = '';
        this.giftAnnouncement = '';
        this.enqueue(true);
        return;
      }

      if (!toggle || !this.operational) {
        return;
      }

      this.clearGiftFocusIntent();
      this.lastError = '';
      this.enqueue(toggle.checked);
    }

    async onClick(event) {
      const withoutMembership = event.target.closest('[data-aerre-rewards-checkout-without]');
      const checkout = event.target.closest('[data-aerre-rewards-checkout]');
      const staleManagedState = this.hasManagedStaleState(this.cart);
      const shouldHandleCheckout = this.operational || staleManagedState;

      if (withoutMembership && shouldHandleCheckout) {
        event.preventDefault();
        await this.checkout(false, true);
        return;
      }

      if (checkout && shouldHandleCheckout) {
        const target = this.operational ? this.desired : false;
        event.preventDefault();
        await this.checkout(target, !target);
      }
    }

    onSectionLoad() {
      this.updateUi();
    }

    async checkout(target, strictRemoval) {
      if (this.checkoutInProgress) {
        return;
      }

      this.checkoutInProgress = true;
      this.updateUi();

      try {
        await this.enqueue(target);
        const inspection = this.inspectCart(this.cart);

        if (strictRemoval && (inspection.membershipItems.length > 0 || inspection.giftItems.length > 0)) {
          throw new Error('Aerre Rewards membership or gift remains in the cart');
        }

        if (!this.membershipStateSafeForCheckout(this.cart)) {
          this.lastError = this.config.strings.error;
          this.cart = await this.removeManagedCartLines(this.cart);
          this.confirmed = this.inspectCart(this.cart).coherent;
          this.publish(this.cart);
        }

        if (!this.membershipStateSafeForCheckout(this.cart)) {
          throw new Error('Aerre Rewards membership state remains unsafe for checkout');
        }

        if (!this.giftStateSafeForCheckout(this.cart, target)) {
          this.giftLastError = this.config.strings.giftError;
          this.giftAnnouncement = '';
          this.cart = await this.removeManagedGifts(this.cart);
          this.confirmed = this.inspectCart(this.cart).coherent;
          this.publish(this.cart);
        }

        if (!this.giftStateSafeForCheckout(this.cart, target)) {
          throw new Error('Aerre Rewards gift state remains unsafe for checkout');
        }

        window.location.assign(this.config.checkoutUrl);
      } catch (error) {
        this.lastError = this.config.strings.error;
        console.error('Aerre Rewards checkout:', error);
      } finally {
        this.checkoutInProgress = false;
        this.updateUi();
      }
    }

    onCartUpdated(event) {
      if (!event.detail || !event.detail.cart || event.detail.aerreRewards) {
        return;
      }

      const previous = this.inspectCart(this.cart);
      const incoming = this.normalizeCart(event.detail.cart);
      const next = this.inspectCart(incoming);
      this.cart = incoming;
      this.confirmed = next.coherent;
      const incomingGiftVariantId = this.confirmedGiftVariantId(incoming) || this.preferredGiftVariantId(incoming);

      if (!this.pending && incomingGiftVariantId) {
        this.desiredGiftVariantId = incomingGiftVariantId;
      }

      const treatmentEligible = this.adapter.countryEligible && this.adapter.cohort === 'treatment' && !this.config.entitled;

      if (!treatmentEligible) {
        if (this.hasExperimentState(incoming)) {
          this.enqueue(false);
        } else {
          this.updateUi();
        }
        return;
      }

      if (!this.operational) {
        if (this.hasManagedStaleState(incoming)) {
          this.enqueue(false);
        } else {
          this.updateUi();
        }
        return;
      }

      const explicitLineRemoval = previous.coherent && !next.coherent && next.hasMerchandise;
      const target = explicitLineRemoval ? false : this.desiredFromCart(incoming);
      this.enqueue(target);
    }
  }

  class AerreRewardsMount extends HTMLElement {
    connectedCallback() {
      window.requestAnimationFrame(() => namespace.controller?.updateUi());
    }
  }

  namespace.controller = new AerreRewardsController(namespace.config);

  if (!window.customElements.get('aerre-rewards-mount')) {
    window.customElements.define('aerre-rewards-mount', AerreRewardsMount);
  }

  namespace.controller.start();
})();
